import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ArrowUp01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ReactNode, useState } from "react";
import { Hide, IconButton, IconButtonSize, Show } from "shared/components";
import { cx } from "shared/utils";

type Column<T> = {
  header: ReactNode;
  accessor: (row: T) => ReactNode;
  widthClass?: string;
};

type ExpandableTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  renderExpanded: (row: T) => ReactNode;
  cellClassName?: string;
  rowClassName?: string;
  headerRowClassName?: string;
  hideHeader?: boolean;
};

export function ExpandableTable<T extends object>(
  props: ExpandableTableProps<T>
) {
  const { columns, rows, renderExpanded, hideHeader } = props;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="space-y-1">
      <Hide if={hideHeader}>
        <div
          className={cx(
            "flex border-b border-alpha-100",
            props.headerRowClassName
          )}
        >
          {columns.map((col, i) => (
            <p
              key={i}
              className={cx(props.cellClassName, col.widthClass ?? "flex-1")}
            >
              {col.header}
            </p>
          ))}
          <div className={cx(props.cellClassName, "w-12 shrink-0")} />
        </div>
      </Hide>

      {rows.map((row, i) => (
        <div key={i}>
          <div
            className={cx(
              "flex cursor-pointer hover:bg-alpha-20 h-[72px]",
              props.rowClassName
            )}
            onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
          >
            {columns.map((col, j) => (
              <p
                key={j}
                className={cx(props.cellClassName, col.widthClass ?? "flex-1")}
              >
                {col.accessor(row)}
              </p>
            ))}
            <div
              key={`expander_${i}`}
              className={cx(props.cellClassName, "w-12")}
            >
              <IconButton
                HugeIcon={expandedIndex === i ? ArrowUp01Icon : ArrowDown01Icon}
                size={IconButtonSize.SM}
                className="bg-white"
              />
            </div>
          </div>

          <Show if={expandedIndex === i}>{renderExpanded(row)}</Show>
        </div>
      ))}
    </div>
  );
}
